import { useEffect, useRef, useState } from "react";
import { apiVariables } from "../../services/api/apiVariables";
import { getToken } from "../../utils/tokenUtils";
import { PhotoImage } from "./PhotoImage";

interface LikeItem {
  id: number;
  userId: string;
  userName?: string;
  userImage?: string;
  profilePhotoId?: string | number;
  createdAt: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  photoId: string;
}

export default function LikesModal({ isOpen, onClose, photoId }: Props) {
  const [likes, setLikes] = useState<LikeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  function getInitials(name: string) {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.substring(0, 2).toUpperCase();
  }

  useEffect(() => {
    if (!isOpen || !photoId) return;

    let mounted = true;
    const controller = new AbortController();

    const loadLikes = async () => {
      setLoading(true);
      try {
        const url = `${apiVariables.PhotoLikeBy}/${photoId}/likesBy`;
        const token = await getToken();
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!res.ok) {
          if (mounted) setLikes([]);
          return;
        }

        const json = await res.json();
        const arr = Array.isArray(json?.data) ? json.data : [];

        const mapped: LikeItem[] = arr.map((l: any) => ({
          id: l.id || l.photoLikeId || Date.now(),
          userId: l.userId || l.likedByUserId,
          userName:
            l.user
              ? `${l.user.firstName || ""} ${l.user.lastName || ""}`.trim()
              : l.userName,
          userImage: l.user?.profileImageUrl || l.profileImageUrl,
          profilePhotoId: l.user?.profileImageId || undefined,
          createdAt: l.createdAt || l.updatedAt,
        }));

        if (mounted) setLikes(mapped);
      } catch {
        if (mounted) setLikes([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadLikes();
    return () => {
      mounted = false;
      controller.abort();
    };
  }, [isOpen, photoId]);

  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => {
      if (listRef.current) {
        listRef.current.scrollTop = 0;
      }
    }, 100);
  }, [isOpen, likes.length]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

        <div className="relative w-full bg-white rounded-t-lg shadow-xl p-4 md:p-6 max-h-[90vh] flex flex-col overflow-hidden"
                style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Likes</h3>
          <button onClick={onClose} className="text-gray-600">✕</button>
        </div>

        {/* List */}
        <div
           ref={listRef}
          // className="overflow-y-auto space-y-4 pb-4"
          className="flex-1 overflow-y-auto space-y-4 pb-4"
        >
          {loading ? (
            <div className="text-center py-6">Loading likes...</div>
          ) : likes.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-6">
              No likes yet
            </div>
          ) : (
            likes.map((l) => (
              <div key={l.id} className="flex gap-3 items-center">
                <PhotoImage
                  photoId={l.profilePhotoId}
                  alt={l.userName || 'User'}
                  className="w-10 h-10 rounded-full object-cover"
                  fallback={
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
                      {getInitials(l.userName || "")}
                    </div>
                  }
                />

                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">
                    {l.userName || "User"}
                  </div>
                  <div className="text-xs text-gray-400">
                    {getDateTimeLabel(l.createdAt)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


function getDateLabel(date: Date): string {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const sameDay = (a: Date, b: Date) =>
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear();

  if (sameDay(date, today)) return "Today";
  if (sameDay(date, yesterday)) return "Yesterday";

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getDateTimeLabel(dateString: string): string {
  const date = parseApiDate(dateString);
  const time = date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${getDateLabel(date)}, ${time}`;
}

function parseApiDate(dateString: string): Date {
  if (!dateString) return new Date();
  const hasTZ = /Z$|[+-]\d{2}:\d{2}$/.test(dateString);
  return new Date(hasTZ ? dateString : `${dateString}Z`);
}
